<template>
  <div>
    <v-alert type="success" v-if="endDate">
      {{ canAddAbstract ? $t('resumesEndDate') + endDate :$t('closedResumes') }}
    </v-alert>
    <v-dialog
          v-model="showDeleteDialog"
          persistent
          width="50%"
          attach=".v-application"
        >
          <v-card>
            <v-card-title class="text-h5">
              {{$t('resumeDeleteTitle')}}
            </v-card-title>
            <v-card-text>
              {{$t('resumeDeleteMessage')}}
            </v-card-text>
            <v-card-actions>
              <v-row>
                <v-col>
                  <v-btn
                    color="green-darken-1"
                    variant="text"
                    @click="showDeleteDialog=false;handleDeleteConfirmation()"
                  >
                    {{$t('resumeDelete')}}
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn
                    color="green-darken-1"
                    variant="text"
                    @click="showDeleteDialog=false;"
                  >
                  {{$t('resumeCancel')}}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
    </v-dialog>
    <v-dialog
          v-model="showConfirmDialog"
          persistent
          width="50%"
          attach=".v-application"
        >
          <v-card>
            <v-card-title class="text-h5">
              {{$t('resumeEditConfirmTitle')}}
            </v-card-title>
            <v-card-text>
              {{$t('resumeEditConformMessage')}}
            </v-card-text>
            <v-card-actions>
              <v-row>
                <v-col>
                  <v-btn
                    color="green-darken-1"
                    variant="text"
                    @click="showConfirmDialog=false;handleEditConfirmation()"
                  >
                    {{$t('resumeSave')}}
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn
                    color="green-darken-1"
                    variant="text"
                    @click="showConfirmDialog=false;closeDialog()"
                  >
                  {{$t('resumeCancel')}}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
    </v-dialog>
    <v-dialog persistent v-model="dialog">
      <template #activator="{ on }">
        <v-btn color="primary" dark v-on="on" @click="showAddDialog" v-if="canAddAbstract">{{
          $t("resumesAdd")
        }}</v-btn>
      </template>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-row>
              <v-col cols="12">
                <v-text-field :counter="200" :label="$t('resumeTitle')" v-model="form.title" :rules="[required, (v) => maxLen(v, 200)]"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select :label="$t('resumeSubjectId')" v-model="form.subjectId" item-value="id" :item-text="translateOption"
                  :items="subjectOptions" :rules="[required]"></v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field :counter="200" :label="$t('resumeAuthor')" v-model="form.author" :rules="[required, (v) => maxLen(v, 200)]"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea :counter="1000" :label="$t('resumeAffiliation')" filled v-model="form.affiliation"
                  :rules="[required, (v) => maxLen(v, 1000)]"></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-text-field :counter="200" :label="$t('resumePresAuthor')" v-model="form.presAuthor"
                  :rules="[required, (v) => maxLen(v, 200)]"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field :label="$t('resumeEmail')" v-model="form.email"
                  :rules="[required, emailRule, (v) => maxLen(v, 200)]"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select :label="$t('resumePresentationId')" v-model="form.presentationId" item-value="id"
                  :item-text="translateOption" :items="presentationOptions" :rules="[required]"></v-select>
              </v-col>
              <v-col cols="12">
                <span>{{ $t('resumeFileMessage') }}</span>
                <v-file-input :label="$t('resumeFile')" v-model="form.file" @change="upload(form.file)" :rules="[required, fileRule]"
                  accept=".doc,.docx,.rtf"></v-file-input>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDialog">{{
            $t("resumeCancel")
          }}</v-btn>
          <v-btn color="blue darken-1" text @click="saveItemWithConfirmIfRequired">{{
            saveButtonLabel
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card v-if="participant.resumes && participant.resumes.length > 0">
      <v-card-title>
        {{ $t("resumesListTitle") }}
      </v-card-title>
      <v-card-text>
        <v-data-table :headers="$t('resumeHeaders')" :items="participant.resumes" :hide-default-footer="true">
          <template #item.title="{ item }">
            <div :title="item.title" class="truncated-text">{{ item.title }}</div>
          </template>
          <template #item.affiliation="{ item }">
            <div :title="item.affiliation" class="truncated-text">{{ item.affiliation }}</div>
          </template>
          <template #item.action="{ item }">
            <div class="d-flex gap-1" v-if="canAddAbstract">
              <v-btn icon small @click="editItem(item)">
                <v-icon color="primary">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="deleteItem(item)">
                <v-icon color="red">mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
          <template #item.subjectId="{ item }">
            {{ getSubjectById(item.subjectId).name }}
          </template>
          <template #item.presentationId="{ item }">
            {{ getPresentationTypeById(item.presentationId).name }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>
      
<script>
import { participant } from "@/model/participant";
import moment from "moment";
export default {
  emits: ["formValid","resumeChanges"],
  props: {
    subjectOptions: {
      type: Array,
      required: true,
    },
    presentationOptions: {
      type: Array,
      required: true,
    },
    endDate : {
      required: false,
    }
  },
  data() {
    return {
      showDeleteDialog: false,
      showConfirmDialog:false,
      validForm: false,
      participant,
      form: {
        // form data for add/edit
        id: null,
        title: "",
        subjectId: "",
        author: "",
        affiliation: "",
        presAuthor: "",
        email: "",
        presentationId: "",
        file: null,
      },
      dialog: false, // whether to show the add/edit dialog
      mode: "add", // current mode (add or edit)
      selectedIndex: -1, // index of the selected item in the list
    };
  },
  watch: {},
  computed: {
    actionButtonLabel() {
      return this.mode === "add"
        ? this.$t("resumesAdd")
        : this.$t("resumeEdit");
    },
    saveButtonLabel() {
      return this.mode === "add"
        ? this.$t("resumeSave")
        : this.$t("resumeUpdate");
    },
    formTitle() {
      return this.mode === "add"
        ? this.$t("resumesAdd")
        : this.$t("resumeEdit");
    },
    canAddAbstract() { 
      if(!this.endDate){
        return true;
      }
      const endDateDate = moment(this.endDate, "YYYY-MM-DD");
      const now = moment();
      return !now.isAfter(endDateDate);
    }
  },
  methods: {
    upload(file){
      if(file) {
        var reader = new FileReader();
        reader.onloadend = ()=> {
          this.form.fileData = reader.result.split(',')[1];
        }
        reader.readAsDataURL(file);
      }else{
        this.form.fileData = undefined;
      }
    },
    translateOption(item) {
      return this.$t(`${item.name}`);
    },
    getSubjectById(id) {
      return this.subjectOptions.find(opt => opt.id == id);
    },
    getPresentationTypeById(id) {
      return this.presentationOptions.find(opt => opt.id == id);
    },
    maxLen(value, len) {
      if (value.length > len) {
        return 'Maximum length is ' + len;
      } else {
        return true;
      }
    },
    emitToggleDialogEvent() {
      this.$emit('dialog-shown', this.dialog);
    },
    validate: function () {
      this.$refs.form.validate();
    },
    required: function (value) {
      if (value) {
        return true;
      } else {
        return this.$t("error.required");
      }
    },
    emailRule: function (value) {
      // Regular expression pattern for email validation
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      // Test the email against the pattern and return the result
      if (pattern.test(value)) {
        return true;
      } else {
        return this.$t("error.emailFormat");
      }
    },
    fileRule: function (value) {
      if (!value) {
        return this.$t("error.required");
      }
      console.log("value.size",value.size);
      // less than 1MB
      if (value.size > 1024 * 1000) {
        return this.$t("error.fileSizemax1M");
      }
      //doc or docx file
      if (!/\.(doc|docx|rtf)$/i.test(value.name)) {
        return this.$t("error.fileTypeDoc");
      }
      return true;
    },
    showAddDialog() {
      this.mode = "add";
      this.selectedIndex = -1;
      this.form = {
        id: null,
        title: "",
        subjectId: "",
        author: "",
        affiliation: "",
        presAuthor: "",
        email: "",
        presentationId: "",
        file: null,
        fileData : null
      };
      this.dialog = true;
      setTimeout(() => {
        this.emitToggleDialogEvent();
      }, 250)
    },
    editItem(item) {
      this.mode = "edit";
      this.selectedIndex = this.participant.resumes.indexOf(item);
      this.form = Object.assign({}, item); // make a copy of the item to edit
      this.dialog = true;
    },
    deleteItem(item) {
      const index = this.participant.resumes.indexOf(item);
      this.selectedIndex =index;
      this.showDeleteDialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.mode === "add";
      setTimeout(() => {
        this.emitToggleDialogEvent();
      }, 250)
    },
    saveItemWithConfirmIfRequired() {
      if(!(this.mode === "add")){
        this.showConfirmDialog = true;
      }else{
        this.saveItem();
      }
    },
    saveItem() {
      if (this.$refs.form.validate()) {
        this.form.file = {
          data : this.form.fileData,
          name : this.form.file.name,
          size : this.form.file.size,
          type : this.form.file.type
        }
        delete this.form.fileData;
        if (this.mode === "add") {
          this.form.id = this.participant.resumes.length + 1;
          this.participant.resumes.push(this.form);
        } else {
          this.participant.resumes.splice(this.selectedIndex, 1, this.form);
        }
        this.dialog = false;
        this.mode === "add"
        setTimeout(() => {
          this.emitToggleDialogEvent();
        }, 250);
        this.$emit("resumeChanges", true);
      }

    },
    handleDeleteConfirmation() {
      if (this.selectedIndex >= 0) {
        this.participant.resumes.splice(this.selectedIndex, 1);
        this.$emit("resumeChanges", true);
      }
      this.selectedIndex = -1;
      this.showDeleteDialog = false;
    },
    handleEditConfirmation(){
      this.saveItem();
      this.closeDialog();
    }
  },
  mounted() {
    this.$emit("formValid", true);
  },
  created() { },
};
</script>
<style scoped lang="css">
.truncated-text {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden; 
  width: 100px;
}
</style>